import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  createGlobalStyle,
  deleteGlobalStyleById,
  fetchGlobalStyleById,
  fetchGlobalStyleList,
  updateGlobalStyleById,
} from './thunks';
import type { GlobalStyle, GlobalStyleSliceInitialState } from './types';
import type { StyleColors } from '@builder4/shared/types/styles';

const initialState: GlobalStyleSliceInitialState = {
  activeGlobalStyle: null,
  globalStylesList: [],
  createGlobalStyleLoading: false,
  readGlobalStyleLoading: false,
  updateGlobalStyleLoading: false,
  deleteGlobalStyleLoading: false,
  loading: false,
  // Edit style confirmation modal
  editStyleModalOpen: false,
  editStyleModalData: null,
  editingStyleColors: null,
};

const globalStyleSlice = createSlice({
  name: 'globalstyles',
  initialState,
  reducers: {
    setActiveGlobalStyle(state, action) {
      state.activeGlobalStyle = action.payload;
    },
    openEditStyleModal(state, action: PayloadAction<GlobalStyle>) {
      state.editStyleModalOpen = true;
      state.editStyleModalData = action.payload;
    },
    closeEditStyleModal(state) {
      state.editStyleModalOpen = false;
      state.editStyleModalData = null;
    },
    setEditingStyleColors(state, action: PayloadAction<StyleColors>) {
      state.editingStyleColors = action.payload;
    },
    clearEditingStyleColors(state) {
      state.editingStyleColors = null;
    },
  },
  extraReducers: (builder) => {
    // --- Create Global Style ---
    builder
      .addCase(createGlobalStyle.pending, (state) => {
        state.createGlobalStyleLoading = true;
      })
      .addCase(createGlobalStyle.fulfilled, (state, action) => {
        state.createGlobalStyleLoading = false;
        state.globalStylesList = [action.payload, ...state.globalStylesList];
      })
      .addCase(createGlobalStyle.rejected, (state) => {
        state.createGlobalStyleLoading = false;
      });

    // --- Fetch Global Style by ID ---
    builder
      .addCase(fetchGlobalStyleById.pending, (state) => {
        state.readGlobalStyleLoading = true;
      })
      .addCase(fetchGlobalStyleById.fulfilled, (state, action) => {
        state.readGlobalStyleLoading = false;
        state.activeGlobalStyle = action.payload;
      })
      .addCase(fetchGlobalStyleById.rejected, (state) => {
        state.readGlobalStyleLoading = false;
      });
    // --- Update Global Style ---
    builder
      .addCase(updateGlobalStyleById.pending, (state) => {
        state.updateGlobalStyleLoading = true;
      })
      .addCase(updateGlobalStyleById.fulfilled, (state, action) => {
        state.updateGlobalStyleLoading = false;
        const index = state.globalStylesList.findIndex((style) => style.id === action.payload.id);

        if (index !== -1) {
          state.globalStylesList[index] = action.payload;
        }
      })
      .addCase(updateGlobalStyleById.rejected, (state) => {
        state.updateGlobalStyleLoading = false;
      });
    // --- Fetch Global Style List ---
    builder
      .addCase(fetchGlobalStyleList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchGlobalStyleList.fulfilled, (state, action) => {
        state.loading = false;
        state.globalStylesList = action.payload;
      })
      .addCase(fetchGlobalStyleList.rejected, (state) => {
        state.loading = false;
      });
    // --- Delete Global Style ---
    builder
      .addCase(deleteGlobalStyleById.pending, (state) => {
        state.deleteGlobalStyleLoading = true;
      })
      .addCase(deleteGlobalStyleById.fulfilled, (state, action) => {
        state.deleteGlobalStyleLoading = false;
        const index = state.globalStylesList.findIndex((style) => style.id === action.payload.id);
        if (index !== -1) {
          state.globalStylesList.splice(index, 1);
        }
      })
      .addCase(deleteGlobalStyleById.rejected, (state) => {
        state.deleteGlobalStyleLoading = false;
      });
  },
});
export const {
  setActiveGlobalStyle,
  openEditStyleModal,
  closeEditStyleModal,
  setEditingStyleColors,
  clearEditingStyleColors,
} = globalStyleSlice.actions;
export default globalStyleSlice.reducer;
